import { EventFeedSort } from '@api/api/event-feed';
import { Button } from '@design/components/button/Button';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { Input } from '@design/components/input/Input';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { useFeedFilters } from '@terminal/stores/use-feed-filters/useFeedFilters';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EventFeedSortDropdown.module.scss';

type SortOption = {
  label: string;
  translationKey?: string;
  value: EventFeedSort | null;
};

const MAIN_OPTIONS: SortOption[] = [
  { label: 'Latest', translationKey: 'ranking.latest', value: null },
  {
    label: 'Favor recent',
    translationKey: 'ranking.favorRecent',
    value: { type: 'timedecay', level: -1 },
  },
  {
    label: 'Balanced',
    translationKey: 'ranking.balanced',
    value: { type: 'timedecay', level: 0 },
  },
  {
    label: 'Favor my interests',
    translationKey: 'ranking.favorMyInterests',
    value: { type: 'timedecay', level: 1 },
  },
  {
    label: 'For you',
    translationKey: 'ranking.forYou',
    value: { type: 'timedecay', level: 2 },
  },
];

const DEBUG_OPTIONS: SortOption[] = [
  { label: 'Pure Chronological', value: null },
  {
    label: 'Time Decay: Heavy Chronological',
    value: { level: -2, type: 'timedecay' },
  },
  {
    label: 'Time Decay: Light Chronological',
    value: { level: -1, type: 'timedecay' },
  },
  { label: 'Time Decay: Balanced', value: { level: 0, type: 'timedecay' } },
  {
    label: 'Time Decay: Light Affinity',
    value: { level: 1, type: 'timedecay' },
  },
  {
    label: 'Time Decay: Heavy Affinity',
    value: { level: 2, type: 'timedecay' },
  },
  {
    label: 'Weight: Heavy Chronological',
    value: { level: -2, type: 'weight' },
  },
  {
    label: 'Weight: Light Chronological',
    value: { level: -1, type: 'weight' },
  },
  { label: 'Weight: Balanced', value: { level: 0, type: 'weight' } },
  { label: 'Weight: Light Affinity', value: { level: 1, type: 'weight' } },
  { label: 'Weight: Heavy Affinity', value: { level: 2, type: 'weight' } },
];

const SORT_OPTIONS = [...MAIN_OPTIONS, ...DEBUG_OPTIONS];

export interface EventFeedSortMenuProps {
  sort: EventFeedSort | null;
  sortOptionId: string | null;
  onSelect: (sort: EventFeedSort | null, id: string) => void;
  label?: string;
  triggerTestId?: string;
}

export const EventFeedSortMenu = ({
  sort,
  sortOptionId,
  onSelect,
  label = 'Sort catalysts',
  triggerTestId = 'event-feed-sort-trigger',
}: EventFeedSortMenuProps) => {
  const { t } = useTranslation('stream');
  const [isOpen, setIsOpen] = useState(false);
  const dismissingMenu = useRef(false);
  useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissingMenu.current) {
        // The enclosing drawer listens on window keyup, after Radix restores focus.
        event.stopImmediatePropagation();
        dismissingMenu.current = false;
      }
    };
    window.addEventListener('keyup', handleKeyUp, true);
    return () => window.removeEventListener('keyup', handleKeyUp, true);
  }, []);

  const getLabel = (option: SortOption) =>
    option.translationKey
      ? t(option.translationKey, option.label)
      : option.label;

  const selectHandler = (id: string) => {
    const option = SORT_OPTIONS.find(option => option.label === id);
    if (option) {
      onSelect(option.value, option.label);
    }
  };

  const selectedOption =
    SORT_OPTIONS.find(option => option.label === sortOptionId) ??
    SORT_OPTIONS.find(
      option =>
        option.value?.level === sort?.level && option.value?.type === sort?.type
    ) ??
    MAIN_OPTIONS[0];

  const renderOption = (option: SortOption) => (
    <RadixDropdown.RadioItem
      key={option.label}
      value={option.label}
      className={styles.option}
      onSelect={() => selectHandler(option.label)}
    >
      {getLabel(option)}
    </RadixDropdown.RadioItem>
  );

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={setIsOpen}
      className={styles.menu}
      modal={false}
      contentProps={{
        'aria-label': label,
        collisionPadding: 8,
        onEscapeKeyDown: () => {
          dismissingMenu.current = true;
        },
      }}
      triggerNode={
        <Button
          className={styles.styledSelect}
          label={getLabel(selectedOption)}
          variant="secondary"
          size="small"
          iconName={isOpen ? 'ChevronLightUp' : 'ChevronLightDown'}
          iconPosition="right"
          data-testid={triggerTestId}
          aria-label={`${label}: ${getLabel(selectedOption)}`}
        />
      }
    >
      <RadixDropdown.RadioGroup value={selectedOption.label}>
        {MAIN_OPTIONS.map(renderOption)}
        <RadixDropdown.Separator className={styles.separator} />
        <RadixDropdown.Label className={styles.sectionLabel}>
          {t('ranking.experimental', 'Experimental / debug')}
        </RadixDropdown.Label>
        {DEBUG_OPTIONS.map(renderOption)}
      </RadixDropdown.RadioGroup>
    </DropdownMenu>
  );
};

export const EventFeedSortDropdown = () => {
  const {
    sort,
    sortOptionId,
    setSort,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
  } = useFeedFilters(state => ({
    sort: state.sort,
    sortOptionId: state.sortOptionId,
    setSort: state.setSort,
    dateFrom: state.dateFrom,
    setDateFrom: state.setDateFrom,
    dateTo: state.dateTo,
    setDateTo: state.setDateTo,
  }));

  return (
    <div className={styles.root} data-testid="event-feed-sort-dropdown">
      <div className={styles.dateField}>
        <span className={styles.dateLabel}>From</span>
        <Input
          type="date"
          className={styles.dateInput}
          variant="outline"
          size="medium"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          data-testid="event-feed-date-from"
        />
      </div>
      <div className={styles.dateField}>
        <span className={styles.dateLabel}>To</span>
        <Input
          type="date"
          className={styles.dateInput}
          variant="outline"
          size="medium"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          data-testid="event-feed-date-to"
        />
      </div>
      <EventFeedSortMenu
        sort={sort}
        sortOptionId={sortOptionId}
        onSelect={setSort}
      />
    </div>
  );
};
